import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AreaChart } from '../../components/charts/AreaChart';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui';
import { cn } from '../../lib/utils';
import { trafficQualityService } from '../../services/trafficQuality';
import type { TrafficQualityPeriod, FraudAlert } from '../../types/trafficQuality';

const PERIODS: { key: string; value: TrafficQualityPeriod }[] = [
  { key: 'today', value: 'today' },
  { key: '7d', value: 'last_7_days' },
  { key: '30d', value: 'last_30_days' },
  { key: '90d', value: 'last_90_days' },
];

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-50 text-blue-700',
  auto_blocked: 'bg-red-50 text-red-700',
  dismissed: 'bg-gray-50 text-gray-500',
  confirmed: 'bg-purple-50 text-purple-700',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function ratioColor(ratio: number): string {
  if (ratio >= 70) return 'text-red-600 font-semibold';
  if (ratio >= 40) return 'text-yellow-600 font-medium';
  return 'text-gray-600';
}

function alertLabel(a: FraudAlert, t: TFunction): string {
  const vars = { value: a.entity_value, clicks: a.clicks_involved, ratio: a.bot_ratio };
  switch (a.type) {
    case 'ip_velocity':
      return t('alerts.label.ip_velocity', vars);
    case 'asn_burst':
      return t('alerts.label.asn_burst', vars);
    case 'keyword_anomaly':
      return t('alerts.label.keyword_anomaly', vars);
    case 'source_mismatch':
      return t('alerts.label.source_mismatch', vars);
    default:
      return a.entity_value;
  }
}

type TQReadApi = Pick<
  typeof trafficQualityService,
  'getSummary' | 'getBySource' | 'getByKeyword' | 'getTopSources' | 'getAlerts'
>;

export default function TrafficQualityPage({
  campaignId: campaignIdProp,
  api = trafficQualityService,
  readOnly = false,
  queryKeyPrefix = 'tq',
}: {
  campaignId?: number;
  api?: TQReadApi;
  readOnly?: boolean;
  queryKeyPrefix?: string;
} = {}) {
  const { t } = useTranslation('trafficQuality');
  const { id } = useParams<{ id: string }>();
  const campaignId = campaignIdProp ?? (id ? parseInt(id, 10) : 0);
  const [period, setPeriod] = useState<TrafficQualityPeriod>('last_7_days');

  const summaryQ = useQuery({
    queryKey: [`${queryKeyPrefix}-summary`, campaignId, period],
    queryFn: () => api.getSummary(campaignId, period),
    enabled: campaignId > 0,
  });
  const sourceQ = useQuery({
    queryKey: [`${queryKeyPrefix}-source`, campaignId, period],
    queryFn: () => api.getBySource(campaignId, period),
    enabled: campaignId > 0,
  });
  const keywordQ = useQuery({
    queryKey: [`${queryKeyPrefix}-keyword`, campaignId, period],
    queryFn: () => api.getByKeyword(campaignId, period),
    enabled: campaignId > 0,
  });
  const ipsQ = useQuery({
    queryKey: [`${queryKeyPrefix}-ips`, campaignId, period],
    queryFn: () => api.getTopSources(campaignId, period),
    enabled: campaignId > 0,
  });
  const alertsQ = useQuery({
    queryKey: [`${queryKeyPrefix}-alerts`, campaignId],
    queryFn: () => api.getAlerts(campaignId),
    enabled: campaignId > 0,
  });

  const queryClient = useQueryClient();
  const invalidateAlerts = () => queryClient.invalidateQueries({ queryKey: ['tq-alerts', campaignId] });

  const blockMut = useMutation({
    mutationFn: (alertId: number) => trafficQualityService.blockAlert(campaignId, alertId),
    onSuccess: invalidateAlerts,
  });
  const dismissMut = useMutation({
    mutationFn: (alertId: number) => trafficQualityService.dismissAlert(campaignId, alertId),
    onSuccess: invalidateAlerts,
  });

  if (summaryQ.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (summaryQ.isError) {
    const status = (summaryQ.error as any)?.response?.status;
    if (status === 403) {
      return <Alert variant="warning">{t('errors.locked')}</Alert>;
    }
    return <Alert variant="error">{t('errors.load_failed')}</Alert>;
  }

  const s = summaryQ.data!.summary;
  const isMonitor = summaryQ.data!.operation_mode === 'monitor';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
            {isMonitor && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
                {t('monitor_badge')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {summaryQ.data!.campaign_name} —{' '}
            <Link to={`/campaigns/${campaignId}`} className="text-primary-600 hover:underline">
              {t('back_to_campaign')}
            </Link>
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                period === p.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              {t(`periods.${p.key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Headline stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-500">{t('cards.quality_score')}</div>
          <div className={cn('text-4xl font-bold mt-1', scoreColor(s.quality_score))}>
            {s.quality_score}
            <span className="text-lg text-gray-400">/100</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.quality_score_hint')}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.bot_clicks')}</div>
          <div className="text-4xl font-bold mt-1 text-red-600">{s.bot.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.bot_of_targetable', { percent: s.bot_percentage })}</div>
          {s.filtered > 0 && (
            <div className="text-xs text-gray-400 mt-0.5">
              {t('cards.filtered_note', { count: s.filtered.toLocaleString() })}
            </div>
          )}
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.high_risk')}</div>
          <div className="text-4xl font-bold mt-1 text-orange-600">{s.high_risk.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.avg_fraud_score', { score: s.avg_fraud_score })}</div>
        </Card>
      </div>

      {/* Threat timeline */}
      <Card header={<h2 className="font-semibold text-gray-900">{t('timeline.title')}</h2>}>
        {summaryQ.data!.timeline.length > 0 ? (
          <AreaChart
            data={summaryQ.data!.timeline}
            height={280}
            xAxisKey="date"
            areas={[
              { dataKey: 'human', name: t('timeline.human'), fill: '#dcfce7', stroke: '#16a34a' },
              { dataKey: 'bot', name: t('timeline.bot'), fill: '#fee2e2', stroke: '#dc2626' },
              { dataKey: 'fraud', name: t('timeline.high_risk'), fill: '#ffedd5', stroke: '#ea580c' },
            ]}
          />
        ) : (
          <p className="text-sm text-gray-400 py-8 text-center">{t('timeline.empty')}</p>
        )}
      </Card>

      {/* Alerts feed */}
      <Card header={<h2 className="font-semibold text-gray-900">{t('alerts.title')}</h2>}>
        {alertsQ.data && alertsQ.data.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {alertsQ.data.map((a) => {
              const actionable = a.status === 'open' && !readOnly;
              const busy = blockMut.isPending || dismissMut.isPending;
              return (
                <li key={a.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={cn('px-2 py-0.5 text-xs rounded border shrink-0', SEVERITY_STYLES[a.severity])}>
                      {t(`alerts.severity.${a.severity}`)}
                    </span>
                    <span className="text-sm text-gray-700 truncate">{alertLabel(a, t)}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.status !== 'open' && (
                      <span className={cn('px-2 py-0.5 text-xs rounded', STATUS_STYLES[a.status])}>
                        {t(`alerts.status.${a.status}`)}
                      </span>
                    )}
                    {actionable && (
                      <>
                        <button
                          onClick={() => blockMut.mutate(a.id)}
                          disabled={busy}
                          className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {t('alerts.block')}
                        </button>
                        <button
                          onClick={() => dismissMut.mutate(a.id)}
                          disabled={busy}
                          className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {t('alerts.dismiss')}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-400 py-6 text-center">{t('alerts.empty')}</p>
        )}
        {alertsQ.data && alertsQ.data.length > 0 && (
          <div className="flex items-start gap-1.5 mt-3 text-xs text-gray-400">
            <svg className="w-4 h-4 text-amber-500 shrink-0 mt-px" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <p>
              <Trans
                t={t}
                i18nKey="alerts.block_note"
                components={{ b: <strong className="font-semibold text-gray-600" /> }}
              />
            </p>
          </div>
        )}
      </Card>

      {/* Two-column: by source + by keyword */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card header={<h2 className="font-semibold text-gray-900">{t('by_source.title')}</h2>}>
          <QualityTable
            loading={sourceQ.isLoading}
            rows={(sourceQ.data ?? []).map((r) => ({
              label: r.banner_source,
              total: r.total,
              bots: r.bots,
              ratio: r.bot_ratio,
            }))}
            labelHead={t('by_source.head')}
          />
        </Card>
        <Card header={<h2 className="font-semibold text-gray-900">{t('by_keyword.title')}</h2>}>
          <QualityTable
            loading={keywordQ.isLoading}
            rows={(keywordQ.data ?? []).map((r) => ({
              label: r.utm_term,
              sub: r.banner_source,
              total: r.total,
              bots: r.bots,
              ratio: r.bot_ratio,
            }))}
            labelHead={t('by_keyword.head')}
            emptyText={t('by_keyword.empty')}
          />
        </Card>
      </div>

      {/* Top offending IPs */}
      <Card
        header={
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">{t('top_ips.title')}</h2>
            {!readOnly && (
              <button
                onClick={() => trafficQualityService.exportGoogleAdsIps(campaignId, period)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t('top_ips.export')}
              </button>
            )}
          </div>
        }
      >
        {ipsQ.data && ipsQ.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4">{t('top_ips.ip')}</th>
                  <th className="py-2 pr-4 text-right">{t('top_ips.clicks')}</th>
                  <th className="py-2 pr-4 text-right">{t('top_ips.bot_clicks')}</th>
                  <th className="py-2 text-right">{t('top_ips.bot_pct')}</th>
                </tr>
              </thead>
              <tbody>
                {ipsQ.data.map((ip) => (
                  <tr key={ip.ip} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-mono text-gray-800">{ip.ip}</td>
                    <td className="py-2 pr-4 text-right">{ip.clicks.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-red-600">{ip.bot_clicks.toLocaleString()}</td>
                    <td className={cn('py-2 text-right', ratioColor(ip.bot_ratio))}>{ip.bot_ratio}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-6 text-center">{t('top_ips.empty')}</p>
        )}
      </Card>
    </div>
  );
}

interface QualityRow {
  label: string;
  sub?: string;
  total: number;
  bots: number;
  ratio: number;
}

function QualityTable({
  rows,
  loading,
  labelHead,
  emptyText,
}: {
  rows: QualityRow[];
  loading: boolean;
  labelHead: string;
  emptyText?: string;
}) {
  const { t } = useTranslation('trafficQuality');
  if (loading) {
    return <div className="py-6 flex justify-center"><Spinner /></div>;
  }
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">{emptyText ?? t('table.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-100">
            <th className="py-2 pr-4">{labelHead}</th>
            <th className="py-2 pr-4 text-right">{t('table.clicks')}</th>
            <th className="py-2 pr-4 text-right">{t('table.bots')}</th>
            <th className="py-2 text-right">{t('table.bot_pct')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.label}-${i}`} className="border-b border-gray-50">
              <td className="py-2 pr-4">
                <div className="text-gray-800 truncate max-w-[220px]">{r.label || '—'}</div>
                {r.sub && <div className="text-xs text-gray-400">{r.sub}</div>}
              </td>
              <td className="py-2 pr-4 text-right">{r.total.toLocaleString()}</td>
              <td className="py-2 pr-4 text-right text-red-600">{r.bots.toLocaleString()}</td>
              <td className={cn('py-2 text-right', ratioColor(r.ratio))}>{r.ratio}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
